// src/App.tsx - Updated with public routes
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import { store, RootState } from "./redux/store";
import { checkAuthStatus } from "./redux/slices/authSlice";
import Layout from "./components/Layout";

// Public Pages (no authentication required)
import Home from "./pages/Home";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";

// Private Pages (authentication required)
import Dashboard from "./pages/Dashboard";
import Wallet from "./pages/Wallet";
import Transactions from "./pages/Transactions";
import Deposit from "./pages/Deposit";
import Withdrawal from "./pages/Withdrawal";
import Kyc from "./pages/Kyc";
import Recovery from "./pages/Recovery";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminKyc from "./pages/admin/AdminKyc";
import AdminRecovery from "./pages/admin/AdminRecovery";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import { isAdmin, hasWalletConnected } from "./types";
import AdminTransactions from "./pages/admin/AdminTransactions";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import { setTheme } from "./redux/slices/themeSlice";

// Loading Component
const AppLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
      <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Unverified Route Component (for verify-email page)
const UnverifiedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  // If user is already verified and authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  // If no token or user, redirect to login
  if (!token || !user) {
    return <Navigate to="/login" />;
  }

  // If user exists but not verified, allow access to verification page
  if (!user.email_verified_at) {
    return <>{children}</>;
  }

  return <Navigate to="/dashboard" />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin(user)) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

// KYC Protected Route
const KycProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (user.verification_level < 2) {
    return <Navigate to="/kyc" />;
  }

  return <>{children}</>;
};

// Wallet Protected Route
const WalletProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  if (!hasWalletConnected(user)) {
    return <Navigate to="/wallet" />;
  }

  return <>{children}</>;
};

// Public Route Component (no authentication required)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAppLoading } = useSelector(
    (state: RootState) => state.auth
  );

  if (isAppLoading) {
    return <AppLoading />;
  }

  // If user is authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const { isAppLoading } = useSelector((state: RootState) => state.auth);
  const { mode } = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    // Set theme on app load
    document.documentElement.classList.toggle("dark", mode === "dark");

    // Listen for storage events to sync theme across tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "theme") {
        const newTheme = event.newValue;
        if (newTheme === "light" || newTheme === "dark") {
          dispatch(setTheme(newTheme));
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Check auth status on app load
    dispatch(checkAuthStatus() as any);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch, mode]);

  if (isAppLoading) {
    return <AppLoading />;
  }

  return (
    <div className={mode}>
      <Router>
        <Routes>
          {/* Public Routes (no authentication required) */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <Home />
              </PublicRoute>
            }
          />
          <Route
            path="/about"
            element={
              <PublicRoute>
                <About />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicRoute>
                <Privacy />
              </PublicRoute>
            }
          />
          <Route
            path="/terms"
            element={
              <PublicRoute>
                <Terms />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <UnverifiedRoute>
                <VerifyEmail />
              </UnverifiedRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Protected Routes (authentication required) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <Layout>
                  <Wallet />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <Layout>
                  <Transactions />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/deposit"
            element={
              <ProtectedRoute>
                <KycProtectedRoute>
                  <WalletProtectedRoute>
                    <Layout>
                      <Deposit />
                    </Layout>
                  </WalletProtectedRoute>
                </KycProtectedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/withdraw"
            element={
              <ProtectedRoute>
                <KycProtectedRoute>
                  <WalletProtectedRoute>
                    <Layout>
                      <Withdrawal />
                    </Layout>
                  </WalletProtectedRoute>
                </KycProtectedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/kyc"
            element={
              <ProtectedRoute>
                <Layout>
                  <Kyc />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/recovery"
            element={
              <ProtectedRoute>
                <KycProtectedRoute>
                  <WalletProtectedRoute>
                    <Layout>
                      <Recovery />
                    </Layout>
                  </WalletProtectedRoute>
                </KycProtectedRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Layout>
                  <AdminDashboard />
                </Layout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <Layout>
                  <AdminUsers />
                </Layout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/kyc"
            element={
              <AdminRoute>
                <Layout>
                  <AdminKyc />
                </Layout>
              </AdminRoute>
            }
          />

          <Route
            path="/admin/recovery"
            element={
              <AdminRoute>
                <Layout>
                  <AdminRecovery />
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/transactions"
            element={
              <AdminRoute>
                <Layout>
                  <AdminTransactions />
                </Layout>
              </AdminRoute>
            }
          />

          {/* Redirect unknown routes to home page */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: mode === "dark" ? "#1F2937" : "#FFFFFF",
            color: mode === "dark" ? "#FFFFFF" : "#1F2937",
            border: `1px solid ${mode === "dark" ? "#374151" : "#E5E7EB"}`,
          },
        }}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
